import {
  buildCsvDownloadFilename,
  downloadCsvText,
} from '@highimpact/react-hooks';
import type { ExportCsvFormValues } from '@highimpact/scatterplot';
import { ExportCsvDialog } from '@highimpact/scatterplot';
import { useCallback, useMemo, useRef, useState } from 'react';

import { exportPlotlyWidgetToPng } from '../../../../report/widgetExportHelpers';
import type { WidgetInstanceCapabilities } from '../../../../runtime/instanceRegistry/types';
import { useRegisterWidgetInstance } from '../../../../runtime/instanceRegistry/useRegisterWidgetInstance';
import { buildExportCsvTransportRequest } from './buildExportCsvTransportRequest';
import { usePlotMeasureTransport } from './usePlotMeasureTransport';

// -- type definitions --

type PlotMeasureTransport = ReturnType<
  typeof usePlotMeasureTransport
>['transport'];

export interface ScatterPlotExportContext {
  readonly dashboardElementId: number;
  readonly widgetId: string;
  readonly plotName: string;
  readonly transport: PlotMeasureTransport;
  readonly curvePrefs: Record<string, { displayName?: string }>;
  readonly curveToSourceIndex: Record<string, number>;
  readonly loadedInterval: [number, number] | undefined;
  readonly visibleInterval: [number, number] | undefined;
  readonly enabled: boolean;
}

interface PendingCsvExport {
  readonly resolve: (value: string | null) => void;
  readonly reject: (error: unknown) => void;
  readonly signal?: AbortSignal;
}

// -- hook --

/**
 * Register a scatter plot widget instance.
 *
 * The following capabilities are registered:
 * - for-report content extraction: PNG format
 * - toolbar actions: export menu, download PNG
 */
export function useScatterPlotInstanceRegistration(
  ctx: ScatterPlotExportContext
) {
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const pendingCsvExportRef = useRef<PendingCsvExport | null>(null);

  const exportCsvText = useCallback(
    async (formValues: ExportCsvFormValues, signal?: AbortSignal) => {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      return ctx.transport.exportCsv(
        buildExportCsvTransportRequest({
          formValues,
          curvePrefs: ctx.curvePrefs,
          curveToSourceIndex: ctx.curveToSourceIndex,
          timeRanges: {
            loaded: ctx.loadedInterval,
            visible: ctx.visibleInterval,
          },
        })
      );
    },
    [
      ctx.curvePrefs,
      ctx.curveToSourceIndex,
      ctx.loadedInterval,
      ctx.transport,
      ctx.visibleInterval,
    ]
  );

  const exportCsvHeadless = useCallback(async () => {
    return exportCsvText({
      selectedCurveIds: Object.keys(ctx.curvePrefs),
      timeRangeChoice: 'visible',
      decimation: { enabled: true, points: 2000 },
    });
  }, [ctx.curvePrefs, exportCsvText]);

  const openCsvDialogForReport = useCallback(
    (signal?: AbortSignal) =>
      new Promise<string | null>((resolve, reject) => {
        if (signal?.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        const onAbort = () => {
          pendingCsvExportRef.current = null;
          setCsvDialogOpen(false);
          resolve(null);
        };

        signal?.addEventListener('abort', onAbort, { once: true });
        pendingCsvExportRef.current = {
          resolve: (value) => {
            signal?.removeEventListener('abort', onAbort);
            resolve(value);
          },
          reject: (error) => {
            signal?.removeEventListener('abort', onAbort);
            reject(error);
          },
          signal,
        };
        setCsvDialogOpen(true);
      }),
    []
  );

  const openCsvDialogForToolbar = useCallback(() => {
    setCsvDialogOpen(true);
  }, []);

  const capabilities = useMemo<WidgetInstanceCapabilities | null>(() => {
    if (!ctx.enabled) {
      return null;
    }

    return {
      dashboardElementId: ctx.dashboardElementId,
      widgetId: ctx.widgetId,
      toolbarActions: [
        {
          id: 'export-menu',
          buttonIcon: 'chevron-down',
          tooltipText: 'Exporter',
          order: 10,
          performAction: () => openCsvDialogForToolbar(),
        },
        {
          id: 'download-png',
          buttonIcon: 'download',
          tooltipText: 'Télécharger en PNG',
          order: 20,
          performAction: async () => {
            const dataUrl = await exportPlotlyWidgetToPng(ctx.widgetId);
            const anchor = document.createElement('a');
            anchor.href = dataUrl;
            anchor.download = `${ctx.plotName}.png`;
            anchor.click();
          },
        },
      ],
      reportExports: [
        {
          format: 'png',
          export: async () => exportPlotlyWidgetToPng(ctx.widgetId),
        },
        {
          format: 'csv',
          supportsInteractive: true,
          export: async ({ mode, signal }) => {
            if (mode === 'headless') {
              return exportCsvHeadless();
            }
            return openCsvDialogForReport(signal);
          },
        },
      ],
    };
  }, [
    ctx.dashboardElementId,
    ctx.enabled,
    ctx.plotName,
    ctx.widgetId,
    exportCsvHeadless,
    openCsvDialogForReport,
    openCsvDialogForToolbar,
  ]);

  useRegisterWidgetInstance(capabilities);

  const handleCsvDialogClose = useCallback(() => {
    setCsvDialogOpen(false);
    pendingCsvExportRef.current?.resolve(null);
    pendingCsvExportRef.current = null;
  }, []);

  const handleCsvExport = useCallback(
    async (formValues: ExportCsvFormValues) => {
      try {
        const csvText = await exportCsvText(
          formValues,
          pendingCsvExportRef.current?.signal
        );

        if (pendingCsvExportRef.current) {
          pendingCsvExportRef.current.resolve(csvText);
          pendingCsvExportRef.current = null;
        } else {
          downloadCsvText(csvText, buildCsvDownloadFilename(ctx.plotName));
        }
      } catch (error) {
        pendingCsvExportRef.current?.reject(error);
        pendingCsvExportRef.current = null;
        throw error;
      } finally {
        setCsvDialogOpen(false);
      }
    },
    [ctx.plotName, exportCsvText]
  );

  const csvDialog = (
    <ExportCsvDialog
      open={csvDialogOpen}
      onClose={handleCsvDialogClose}
      curvePrefs={ctx.curvePrefs}
      timeRanges={{
        loaded: ctx.loadedInterval,
        visible: ctx.visibleInterval,
      }}
      onExportCsv={handleCsvExport}
    />
  );

  return { csvDialog };
}
